<section aria-labelledby="application-status-title">
  <header class="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <p class="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-primary">Membership application</p>
      <h2 id="application-status-title" class="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        Application status
      </h2>
      <p class="mt-2 text-sm text-muted-foreground">Your latest application update and essential membership details.</p>
    </div>
    @if (!applicationLoading()) {
      <button hlmBtn type="button" variant="outline" size="sm" (click)="checkApplicationStatus()">
        <ng-icon name="lucideRefreshCw" />
        Refresh status
      </button>
    }
  </header>

  @if (applicationLoading()) {
    <div class="status-loading flex min-h-64 flex-col items-center justify-center gap-4 rounded-2xl border border-border bg-muted/35" aria-live="polite">
      <hlm-spinner class="text-xl" />
      <div class="text-center">
        <p class="font-semibold text-foreground">Checking your application</p>
        <p class="mt-1 text-sm text-muted-foreground">This should only take a moment.</p>
      </div>
    </div>
  } @else {
    <div class="status-summary status-{{ statusPresentation().tone }} rounded-2xl border p-5 sm:p-7">
      <div class="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div class="status-medallion flex size-14 shrink-0 items-center justify-center rounded-full">
          <ng-icon size="26" [name]="statusPresentation().icon" />
        </div>

        <div class="min-w-0 flex-1">
          <span hlmBadge variant="outline" class="status-badge">{{ statusPresentation().badge }}</span>
          <h3 class="mt-3 font-heading text-xl font-bold text-foreground sm:text-2xl">{{ statusPresentation().title }}</h3>
          <p class="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{{ statusPresentation().description }}</p>
        </div>
      </div>

      @if (applicationData(); as application) {
        <hlm-separator class="my-6" />

        <dl class="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          <div>
            <dt class="detail-label">Applicant</dt>
            <dd class="detail-value">{{ application.full_name || 'Not provided' }}</dd>
          </div>
          <div>
            <dt class="detail-label">Phone number</dt>
            <dd class="detail-value">{{ application.mobile_number || 'Not provided' }}</dd>
          </div>
          <div>
            <dt class="detail-label">Registration number</dt>
            <dd class="detail-value detail-code">{{ application.registration_number || 'Not assigned' }}</dd>
          </div>
          <div>
            <dt class="detail-label">Serial number</dt>
            <dd class="detail-value detail-code">{{ application.serial_number || 'Not assigned' }}</dd>
          </div>
        </dl>

        @if (hasAdditionalDetails()) {
          <div class="mt-6">
            <button hlmBtn type="button" variant="ghost" size="sm" (click)="toggleDetails()" [attr.aria-expanded]="showDetails()">
              {{ showDetails() ? 'Hide details' : 'More details' }}
              <ng-icon name="lucideChevronDown" class="chevron" [class.chevron-open]="showDetails()" />
            </button>

            @if (showDetails()) {
              <dl class="additional-details mt-3 grid gap-x-8 gap-y-5 rounded-xl border border-border bg-card p-4 sm:grid-cols-2">
                @if (application.region) {
                  <div>
                    <dt class="detail-label">Region</dt>
                    <dd class="detail-value">{{ application.region }}</dd>
                  </div>
                }
                @if (application.approved_by) {
                  <div>
                    <dt class="detail-label">Approved by</dt>
                    <dd class="detail-value">{{ application.approved_by }}</dd>
                  </div>
                }
                @if (application.policy; as policy) {
                  <div>
                    <dt class="detail-label">Policy number</dt>
                    <dd class="detail-value detail-code">{{ policy.policy_number || 'Not assigned' }}</dd>
                  </div>
                  <div>
                    <dt class="detail-label">Policy period</dt>
                    <dd class="detail-value">{{ policy.start_date || 'Not set' }} — {{ policy.end_date || 'Not set' }}</dd>
                  </div>
                }
              </dl>
            }
          </div>
        }
      }
    </div>

    <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
      @if (applicationError()) {
        <button hlmBtn type="button" variant="outline" (click)="checkApplicationStatus()">
          <ng-icon name="lucideRefreshCw" />
          Try again
        </button>
      } @else if (applicationData()) {
        <a hlmBtn routerLink="/dashboard/my-application-form" variant="outline">
          <ng-icon name="lucideFileText" />
          View full application
        </a>
      }
    </div>
  }
</section>
